import { AsyncPipe, CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatLegacyDialog, MatLegacyDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyProgressBarModule } from '@angular/material/legacy-progress-bar';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { MatSidenavModule } from '@angular/material/sidenav';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FeatureFlag, NewPermission } from '@core/enums';
import { QueryConfig } from '@core/models';
import { Store, Actions, ofActionCompleted, ofActionDispatched, ofActionSuccessful } from '@ngxs/store';
import { ColumnSelectorModule } from '@shared/components/column-selector/column-selector.module';
import { DirectivesModule } from '@shared/directives/directives.module';
import { RelatedToType } from '@shared/enums';
import { CustomObjectState } from '@shared/modules/custom-objects/state/state';
import { defaultTaskTableFields } from '@shared/modules/legacy-tasks/list/list.constants';
import { AccountState } from '@state/account/state';
import { UserState } from '@state/user/state';
import { LoadingComponent } from '@tasks/loading/loading.component';
import { TaskExportUtilsService } from '@tasks/services/task-export-utils.service';
import { TaskSidebarConfigService } from '@tasks/services/task-sidebar-config.service';
import { TasksService } from '@tasks/services/tasks.service';
import { TaskActions } from '@tasks/state/actions';
import { TaskState } from '@tasks/state/state';
import { TaskHeaderActionBarComponent } from '@tasks/task-shell/task-header-action-bar/task-header-action-bar.component';
import { TaskSidebarComponent } from '@tasks/task-shell/task-sidebar/task-sidebar.component';
import { TasksEmptyComponent } from '@tasks/tasks-empty/tasks-empty.component';
import {
  TaskViewType,
  TaskType,
  Task,
  TaskView,
  LAST_PER_PAGE_SELECTED,
  TaskRelatedToModule,
  TaskSort,
  TaskQueryFilter,
} from '@tasks/tasks.types';

import cloneDeep from 'lodash-es/cloneDeep';
import { BehaviorSubject, distinctUntilChanged, filter, noop, Observable, skip, switchMap, take, tap } from 'rxjs';
import {
  FilterListItemValueModel,
  TsBadgeModule,
  TsButtonModule,
  TsFilterModule,
  TSIconModule,
  TsInputComponent,
  TsOverlayComponent,
  TsOverlayModule,
  TsPageComponent,
  TsPageHeaderModule,
  TsPaginatorModule,
  TsSnackbarService,
} from 'tsui';
import { TsPageSidenavConfig } from 'tsui/lib/ts-page/ts-page.component';
import { DatadogService } from '@shared/services/datadog.service';
import { TaskUtils } from '@tasks/tasks.utils';
import UrlUtils from '@shared/Utils/url.utils';
import { JupiterState } from 'app/modules/jupiter/@store/state';

@Component({
  selector: 'app-task-shell',
  standalone: true,
  providers: [TaskSidebarConfigService],
  host: {
    '[class.h-full-64]': 'isGlobalTask || isAggregatedTasks',
    '[class.h-full-128]': '!isGlobalTask || isAggregatedTasks',
  },
  imports: [
    CommonModule,
    TsPageComponent,
    TsPageHeaderModule,
    TsButtonModule,
    TsBadgeModule,
    TSIconModule,
    TsFilterModule,
    ColumnSelectorModule,
    TsInputComponent,
    MatLegacyTooltipModule,
    MatLegacyDialogModule,
    MatLegacyProgressBarModule,
    TsOverlayModule,
    DirectivesModule,
    MatSidenavModule,
    RouterOutlet,
    TaskSidebarComponent,
    TaskHeaderActionBarComponent,
    TasksEmptyComponent,
    LoadingComponent,
    AsyncPipe,
    TsPaginatorModule,
  ],
  template: `
    <ts-page [sidenavConfig]="savedViewsSidebarConfig" [displaySidenav]="displaySidenav">
      <app-task-sidebar sidenav></app-task-sidebar>
      <mat-drawer-container main class="h-full">
        <mat-drawer-content class="flex-col bg-white tasks-drawer">
          @if (taskView && !isSettingsPage) {
            <ts-page-header>
              <ng-container page-header-title>
                <section class="flex items-center">
                  <div class="ml-2">
                    <h5 class="m-0 leading-none" data-testid="task-header-view-name">{{ taskView?.name }}</h5>
                    @if (!isAggregatedTasks) {
                      <span>{{ taskCount | async }} tasks</span>
                    }
                  </div>
                </section>
              </ng-container>
              <ng-container page-header-action-buttons>
                <section class="flex flex-row gap-2">
                  @if ((viewType$ | async) !== TaskViewType.Gantt) {
                    <ts-button
                      variant="secondary"
                      icon="ts_file-save"
                      data-dd-action-name="tasks-header-export"
                      (click)="openTaskExportDialog()"
                      [appAuthorize]="getTaskListSharingRecordsPermission()"
                      data-testid="export-tasklist-button"
                      >Export</ts-button
                    >
                  }
                  @if (!isAggregatedTasks) {
                    <ts-button
                      icon="add"
                      color="primary"
                      [matTooltip]="'Add new section'"
                      (click)="createTask({ taskType: 'section' })"
                      data-dd-action-name="tasks-header-add-section"
                      data-testid="add-section-button"
                    >
                      Add Section
                    </ts-button>
                  }
                </section>
              </ng-container>
              <ng-container page-header-action-bar>
                <app-task-header-action-bar
                  [allowSharingAndExport]="getTaskListSharingRecordsPermission()"
                  (columnsChanged)="onColumnsChanged($event)"
                  (groupByChanged)="onGroupByChanged($event)"
                  (taskViewChange)="onTaskViewChange($event)"
                  (applyFilter)="onApplyFilter($event)"
                  (clearFilter)="onClearFilter()"
                ></app-task-header-action-bar>
              </ng-container>
            </ts-page-header>
          }

          <div style="z-index: 999; height: 5px; width: 100%">
            @if (showProgressBar) {
              <mat-progress-bar mode="indeterminate"></mat-progress-bar>
            }
          </div>

          @if (isLoading) {
            <app-tasks-loading></app-tasks-loading>
          } @else if (taskView || isSettingsPage) {
            <div class="flex flex-col">
              <router-outlet (activate)="subscribeChildEvents($event)"></router-outlet>
              <!-- Aggregated Tasks Footer -->
              <ng-container *ngIf="isAggregatedTasks && pagination$ | async as pagination">
                <ts-paginator
                  [ngClass]="{ loading: isLoading }"
                  [totalItems]="pagination.totalItems"
                  [activePage]="pagination.page"
                  [pageSize]="pagination.pageSize"
                  (pageChanged)="onPageChange($event)"
                ></ts-paginator>
              </ng-container>
            </div>
          } @else {
            <div class="flex h-full bg-white">
              <app-tasks-empty
                [allowTaskListCreation]="canCreateTaskList"
                (createTaskList)="onCreateTaskList()"
              ></app-tasks-empty>
            </div>
          }
        </mat-drawer-content>

        <mat-drawer mode="side" position="end" [disableClose]="true" [opened]="openDetails">
          <router-outlet name="details"></router-outlet>
        </mat-drawer>
      </mat-drawer-container>
    </ts-page>
  `,
  styleUrl: './task-shell.component.scss',
})
export class TaskShellComponent implements OnInit, OnDestroy {
  @ViewChild('filtersOverlayBox') private _filtersOverlayBox: TsOverlayComponent;
  @ViewChild('userAssociationsOverlayBox') private _userAssociationOverlayBox: TsOverlayComponent;

  private store = inject(Store);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private matDialog = inject(MatLegacyDialog);
  private tasksService = inject(TasksService);
  private snackBar = inject(TsSnackbarService);
  private taskExportUtilsService = inject(TaskExportUtilsService);
  private changeTriggers: Set<string> = new Set();
  private destroyRef = inject(DestroyRef);
  private actions$ = inject(Actions);
  private taskSidebarConfigService = inject(TaskSidebarConfigService);
  private dataDogService = inject(DatadogService);
  pagination: { activePage: number; pageSize: number; totalItems?: number };

  taskView: TaskView;
  taskView$: Observable<TaskView> = this.store.select(TaskState.getTaskView);
  task$: Observable<Task> = this.store.select(TaskState.getCurrentTask);
  pagination$: Observable<any> = this.store.select(TaskState.getPagination);

  showSavedView = false;

  savedViewsSidebarConfig: TsPageSidenavConfig = {
    title: '--',
    actions: [],
  };

  filterValueList = new BehaviorSubject<FilterListItemValueModel[]>([]);
  relatedToType = RelatedToType;
  viewType: TaskViewType;
  viewType$ = this.store.select(TaskState.getViewType);
  searchInputControl: FormControl;
  queryFilters: TaskQueryFilter;
  selectedFields;
  groupBy: string | string[] = '';
  showProgressBar = false;
  openDetails = false;
  needsToRefreshTasks = true;
  isLoading = true;
  taskCount = new BehaviorSubject(0);
  TaskViewType = TaskViewType;

  get isAggregatedTasks(): boolean {
    return this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType === TaskRelatedToModule.AggregatedTasks;
  }

  get isGlobalTask(): boolean {
    return this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType === TaskRelatedToModule.Account;
  }

  get isTaskListTemplate(): boolean {
    return this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType === TaskRelatedToModule.TaskListTemplate;
  }

  get displaySidenav(): boolean {
    return !this.isTaskListTemplate;
  }

  get isSettingsPage(): boolean {
    return this.router.url.includes('settings') && !this.isTaskListTemplate;
  }

  get canCreateTaskList(): boolean {
    if (this.isGlobalTask) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_CREATE));
    } else {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_CREATE));
    }
  }

  constructor() {}

  ngOnDestroy(): void {
    this.snackBar?.dismiss();
  }

  ngOnInit(): void {
    this.searchInputControl = new FormControl('');
    this.viewType = this.store.selectSnapshot(TaskState.getViewType);
    this.savedViewsSidebarConfig = this.taskSidebarConfigService.getConfig(
      this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType as TaskRelatedToModule,
    );

    const perPage = +UrlUtils.getFromLocalStorage(LAST_PER_PAGE_SELECTED) || 20;
    this.pagination = { activePage: 1, pageSize: perPage };

    this.pagination$.pipe(takeUntilDestroyed(this.destroyRef), skip(1)).subscribe((pagination) => {
      this.pagination = pagination;
    });

    this.initSidebar();

    this.actions$.pipe(ofActionCompleted(TaskActions.FilterTasks, TaskActions.GetTasks)).subscribe(() => {
      this.isLoading = false;
    });

    this.taskView$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((taskView) => {
      this.taskViewUpdated(taskView);
    });

    this.actions$.pipe(ofActionDispatched(TaskActions.FilterTasks, TaskActions.GetTasks)).subscribe(() => {
      this.showProgressBar = true;
      this.changeDetectorRef.detectChanges();
    });

    this.actions$.pipe(ofActionSuccessful(TaskActions.FilterTasks, TaskActions.GetTasks)).subscribe(() => {
      this.showProgressBar = false;
      this.needsToRefreshTasks = false;
      this.changeDetectorRef.detectChanges();
    });

    this.task$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((task) => (this.openDetails = !!task)),
        filter((task) => !!task),
      )
      .subscribe((task: Task) => {
        this.router.navigate([{ outlets: { details: ['details', task.id] } }], {
          relativeTo: this.activatedRoute,
        });
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(TaskState.getTaskCounters)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((counters) => this.taskCount.next(counters?.[this.taskView?.id]?.currentTaskCount ?? 0)),
      )
      .subscribe(noop);
  }

  subscribeChildEvents(component: any): void {
    if (component.columnsMoved) {
      component.columnsMoved.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((columns) => {
        this.onColumnsChanged(columns);
      });
    }

    if (component.columnsResized) {
      component.columnsResized.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((columns) => {
        this.onColumnsResized(columns);
      });
    }

    if (component.onSortChange) {
      component.onSortChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
        this.onSortChange(event);
      });
    }
  }

  initSidebar(): void {
    this.activatedRoute.params.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged()).subscribe((params) => {
      const taskViewId = +params.taskListId;
      const sidebarItems = this.store.selectSnapshot(TaskState.getSidebarItems);
      const taskView = cloneDeep(sidebarItems.find((view) => view.id === taskViewId));

      this.needsToRefreshTasks = true;
      this.showProgressBar = true;
      this.changeTriggers.clear();
      this.changeDetectorRef.detectChanges();
      this.store.dispatch(new TaskActions.SetTaskView(taskView));
    });

    if (!this.isAggregatedTasks) {
      this.activatedRoute?.children.forEach((childRoute) => {
        if (childRoute.outlet === 'details') {
          childRoute.params.pipe(take(1)).subscribe((params) => {
            this.store.dispatch(new TaskActions.GetTaskDetail(+params['taskListId'], +params['taskId']));
          });
        }
      });
    }

    if (this.isAggregatedTasks) {
      this.showSavedView = true;
      this.savedViewsSidebarConfig.title = 'Saved Views';
    } else {
      this.showSavedView = false;
      this.savedViewsSidebarConfig.title = 'Task Lists';
    }
  }

  taskViewUpdated(taskView: TaskView): void {
    if (!taskView) {
      this.isLoading = false;
      this.showProgressBar = false;
      return;
    }

    this.taskView = taskView;
    this.groupBy = this.taskView.definition?.table?.groupBy ?? '';
    this.queryFilters = this.taskView.definition?.filters
      ? cloneDeep(JSON.parse(this.taskView.definition.filters))
      : {};
    this.selectedFields = [...(this.taskView?.definition?.fields ?? defaultTaskTableFields.map(({ name }) => name))];
    this.queryFilters.sort = this.taskView.definition?.table?.sort;
    const perPage = this.isAggregatedTasks ? UrlUtils.getFromLocalStorage(LAST_PER_PAGE_SELECTED) || 50 : 500;
    this.pagination['pageSize'] = perPage;
    this.queryFilters.pagination = this.pagination;
    if (this.needsToRefreshTasks) {
      const action = new TaskActions.FilterTasks(this.queryFilters as any, true);
      this.store.dispatch(action);
    }

    this.taskCount.next(
      this.store.selectSnapshot(TaskState.getTaskCounters)?.[this.taskView.id]?.currentTaskCount ?? 0,
    );

    this.changeDetectorRef.detectChanges();
  }

  public openTaskExportDialog() {
    this.taskExportUtilsService.openTaskExportDialog().subscribe((result) => {
      this.downloadTasks(result);
    });
  }

  public downloadTasks(format: 'csv' | 'pdf' = 'csv'): void {
    this.store
      .dispatch(
        new TaskActions.FilterTasks(
          {
            ...this.queryFilters,
            format,
          } as any,
          true,
        ),
      )
      .subscribe(() => {
        this.snackBar.open('Your export is being processed and will be emailed to you shortly..');
      });
  }

  getTaskListSharingRecordsPermission() {
    if (!this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return 'TASK_LIST_ADD_USER' as NewPermission;
    }

    if (this.isGlobalTask) {
      return NewPermission.GLOBAL_TASKS_SHARING_RECORDS;
    } else {
      return NewPermission.AGGREGATED_TASKS_SHARING_RECORDS;
    }
  }

  onCreateTaskList() {
    let relatedTo = this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType as string;
    if (relatedTo === TaskRelatedToModule.CustomObjectValue) {
      const selectedCo = this.store.selectSnapshot(CustomObjectState.getValueById);
      relatedTo = selectedCo?.customObject?.name ?? this.store.selectSnapshot(JupiterState.getRelatedToObject).name;
    }
    this.taskSidebarConfigService.openCreateTaskListDialog(relatedTo as TaskRelatedToModule);
  }

  onTaskViewChange(viewName: TaskViewType) {
    this.store.dispatch(new TaskActions.SetViewType(viewName));
  }

  createTask(model: { taskType: TaskType; parentId?: number; rank?: number }) {
    if (model.taskType === 'section') {
      const nextRank = TaskUtils.flattenTasks(this.store.selectSnapshot(TaskState.getTasks)).length + 1;
      model.rank = nextRank;
    }
    this.store.dispatch(new TaskActions.CreateTask(model));
  }

  onSortChange(event: TaskSort[]) {
    this.needsToRefreshTasks = true;
    this.taskView = {
      ...this.taskView,
      definition: {
        ...this.taskView.definition,
        fields: this.selectedFields,
        table: {
          ...this.taskView.definition.table,
          sort: event,
        },
      },
    };
    this.needsToRefreshTasks = true;
    this.store.dispatch(new TaskActions.SetTaskView({ ...this.taskView }));

    this.openTrackStateSnackbar('columns');
  }

  onColumnsChanged(columns: string[]) {
    this.selectedFields = columns;
    this.taskView = {
      ...this.taskView,
      definition: {
        ...this.taskView.definition,
        fields: this.selectedFields,
        table: {
          ...this.taskView.definition.table,
        },
      },
    };

    this.store.dispatch(new TaskActions.SetTaskView({ ...this.taskView }));
    this.openTrackStateSnackbar('columns');
  }

  onColumnsResized(columnsState: any[]) {
    this.taskView = {
      ...this.taskView,
      definition: {
        ...this.taskView.definition,
        fields: this.selectedFields,
        table: {
          ...this.taskView.definition.table,
          columnsState: columnsState,
        },
      },
    };

    this.store.dispatch(new TaskActions.SetTaskView({ ...this.taskView }));
    this.openTrackStateSnackbar('columns');
  }

  onGroupByChanged(groupBy: string | string[]) {
    this.groupBy = groupBy;
    this.taskView = {
      ...this.taskView,
      definition: {
        ...this.taskView.definition,
        fields: this.selectedFields,
        table: {
          ...this.taskView.definition.table,
          groupBy,
        },
      },
    };
    this.needsToRefreshTasks = true;
    this.store.dispatch(new TaskActions.SetTaskView({ ...this.taskView }));

    this.openTrackStateSnackbar('columns');
  }

  onApplyFilter(filters: Partial<QueryConfig.QueryFilters>, trackSnackbar = true): void {
    if (trackSnackbar) {
      this.openTrackStateSnackbar('filters');
    }

    this.queryFilters = cloneDeep(filters);
    this.taskView = {
      ...this.taskView,
      definition: {
        ...this.taskView.definition,
        fields: this.selectedFields,
        groupBy: this.groupBy,
        filters: JSON.stringify(this.queryFilters),
      },
    };
    this.dataDogService.logAction('Tasks - Header - Apply Filter');
    this.store.dispatch(new TaskActions.FilterTasks(this.queryFilters as any, true));
  }

  onClearFilter(): void {
    this.openTrackStateSnackbar('filters');
    this.queryFilters = {
      sort: this.queryFilters.sort,
    };
    this.store.dispatch(new TaskActions.ClearFilter());
    const action = new TaskActions.FilterTasks(this.queryFilters as any, true);
    this.store.dispatch(action);
  }

  onPageChange(event: { activePage: number; pageSize: number }) {
    UrlUtils.saveToLocalStorage(LAST_PER_PAGE_SELECTED, event.pageSize);
    this.queryFilters.pagination = event;
    this.onApplyFilter(this.queryFilters, false);
  }

  private openTrackStateSnackbar(changeType: 'filters' | 'columns'): void {
    this.changeTriggers.add(changeType);
    this.snackBar.openApplyChanges(this.resetToOriginalStates.bind(this), this.saveAllChanges.bind(this));
  }

  private saveAllChanges(): void {
    const newTaskView = {
      ...this.taskView,
      definition: {
        ...this.taskView.definition,
        fields: this.selectedFields,
        table: {
          ...this.taskView.definition.table,
          groupBy: this.groupBy,
        },
        filters: JSON.stringify(this.queryFilters),
      },
    };
    this.store.dispatch(new TaskActions.UpdateTaskView(newTaskView));
  }

  private resetToOriginalStates(): void {
    this.needsToRefreshTasks = true;
    this.store.dispatch(new TaskActions.UndoTaskViewChanges());
    this.changeTriggers.clear();
    this.changeDetectorRef.detectChanges();
  }

  private hasFiltersApplied(): boolean {
    return (
      (!!this.queryFilters.q && this.queryFilters.q !== '') ||
      (this.queryFilters?.fjson?.length && this.queryFilters?.fjson !== '[]') ||
      this.queryFilters?.sort?.length > 0
    );
  }
}
